import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { APP } from '@/constants/testIds';
import { InfoCard } from './common';
import { Sparkles, AlertTriangle, Lightbulb, ListChecks, Clock, RefreshCcw, Loader2, Wand2 } from 'lucide-react';

const ADV_LABEL = {
  suitability: 'Suitability',
  crop_selection: 'Crop selection',
  crop_selection_hires: 'HiRes crop selection',
  sowing_calendar: 'Sowing calendar',
  sowing_isat: 'Weather sowing',
  weather: 'Weather',
  irrigation: 'Irrigation',
  nutrient: 'Nutrient',
  growth: 'NDVI / Growth',
  harvest: 'Harvest',
  market_mandi: 'Mandi',
  market_mandi_compare: 'Mandi compare',
  land_cover: 'Land cover',
  dndc: 'DNDC yield',
};

const ActionPlan = ({ farm, farmer, crops, selectedCrop, season, year }) => {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const cropLabel = crops?.find(c => c.key === selectedCrop)?.label;

  const generate = async () => {
    if (!farm) { toast.error('Select a farm first'); return; }
    setLoading(true);
    try {
      const p = await api.finalPlan({
        farm_id: farm.id,
        farmer_id: farmer?.id,
        crop_key: selectedCrop,
        crop_label: cropLabel,
        season,
        year,
      });
      setPlan(p);
      if (p.source === 'claude') toast.success('Action plan ready');
      else if (p.source === 'empty') toast('Run any advisory first — nothing to summarise yet', { icon: '🌱' });
      else toast.warning('Plan generated in offline mode');
    } catch (e) {
      toast.error('Could not generate action plan');
    } finally { setLoading(false); }
  };

  return (
    <>
      <InfoCard
        title="Farm action plan"
        subtitle="AI synthesis of every advisory you've run on this farm — sowing, weather, irrigation, NDVI, mandi and more."
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button
            data-testid={APP.actions.runFinalPlan}
            onClick={generate}
            disabled={loading}
            className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white shrink-0"
          >
            {loading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Composing your plan…</>
              : plan
                ? <><RefreshCcw className="h-4 w-4 mr-2" /> Refresh plan</>
                : <><Wand2 className="h-4 w-4 mr-2" /> Generate my plan</>}
          </Button>
          <p className="text-xs text-icrisat-earth">
            {farm ? `For ${farm.name || 'this farm'} · ${cropLabel || selectedCrop} · ${season} ${year}` : 'Select a farm to enable.'}
          </p>
        </div>
      </InfoCard>

      {!plan && !loading && (
        <Card className="border-dashed border-icrisat-green/30">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <Sparkles className="h-8 w-8 text-icrisat-green mx-auto mb-2 opacity-70" />
            Your collated action plan will appear here. Ideal to run once a week or after
            major weather changes — it reads every advisory you've completed and boils it
            down to a farmer-friendly checklist.
          </CardContent>
        </Card>
      )}

      {plan && (
        <div data-testid="action-plan-result" className="grid gap-4">
          {/* Hero card */}
          <Card className="border-icrisat-green/40 bg-gradient-to-br from-white to-icrisat-green-soft/40">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-icrisat-green/10 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-icrisat-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium tracking-widest text-icrisat-green uppercase">
                    Action plan · {plan.source === 'claude' ? 'AI-synthesised' : plan.source === 'empty' ? 'no data yet' : 'offline'}
                  </p>
                  <h3 data-testid="action-plan-headline" className="font-display text-2xl text-icrisat-green mt-1">
                    {plan.headline || 'Your farm plan'}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-icrisat-ink leading-relaxed">{plan.summary}</p>

              <div className="flex flex-wrap items-center gap-2 pt-2 text-xs text-icrisat-earth">
                {plan.source_advisories?.map(s => (
                  <span key={s} className="rounded-full bg-white border border-icrisat-green/20 px-2 py-0.5">
                    {ADV_LABEL[s] || s}
                  </span>
                ))}
                {plan.check_again_in_days && (
                  <span className="rounded-full bg-icrisat-green/10 border border-icrisat-green/30 text-icrisat-green px-2 py-0.5 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Check again in {plan.check_again_in_days} days
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top actions */}
          {plan.top_actions?.length > 0 && (
            <PlanSection
              title="Top actions this week"
              Icon={ListChecks}
              accent="green"
              items={plan.top_actions}
              testId="action-plan-actions"
            />
          )}

          {/* Warnings */}
          {plan.warnings?.length > 0 && (
            <PlanSection
              title="Watch out for"
              Icon={AlertTriangle}
              accent="amber"
              items={plan.warnings}
              testId="action-plan-warnings"
            />
          )}

          {/* Opportunities */}
          {plan.opportunities?.length > 0 && (
            <PlanSection
              title="Opportunities"
              Icon={Lightbulb}
              accent="blue"
              items={plan.opportunities}
              testId="action-plan-opportunities"
            />
          )}

          <p className="text-[11px] text-muted-foreground text-center pt-1">
            Generated {plan.generated_at ? new Date(plan.generated_at).toLocaleString('en-IN') : 'just now'}
            {plan.source === 'claude' && ' · Claude Sonnet 4.5 · verify against KVK / PJTSAU AAS'}
          </p>
        </div>
      )}
    </>
  );
};

const ACCENT_CLASSES = {
  green: 'border-icrisat-green/30 bg-white',
  amber: 'border-amber-300 bg-amber-50/60',
  blue:  'border-sky-300 bg-sky-50/60',
};
const ACCENT_ICON = {
  green: 'text-icrisat-green bg-icrisat-green/10',
  amber: 'text-amber-700 bg-amber-100',
  blue:  'text-sky-700 bg-sky-100',
};

const PlanSection = ({ title, Icon, accent = 'green', items, testId }) => (
  <Card className={ACCENT_CLASSES[accent]}>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center gap-2 mb-3">
        <span className={`h-8 w-8 rounded-full flex items-center justify-center ${ACCENT_ICON[accent]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h4 className="font-display text-lg text-icrisat-ink">{title}</h4>
      </div>
      <ul data-testid={testId} className="space-y-2 text-sm text-icrisat-ink leading-relaxed">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-icrisat-green mt-1">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export default ActionPlan;
